import { Component, ElementRef, Input, ViewChild, ViewEncapsulation, type OnDestroy, type OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';

import { LoaderType } from '@fcom/common/';
import { GtmService } from '@fcom/common/gtm';
import { isEmptyObjectOrHasEmptyValues, unsubscribe } from '@fcom/core/utils';
import { FormStatus, PrefillService, trackFormState, TypedFormGroup } from '@fcom/service-forms';
import {
  ATTACHMENT_ALLOWED_FILE_EXTENSIONS,
  ATTACHMENT_MAX_SIZE_IN_BYTES,
  ButtonType,
  NotificationLayout,
  NotificationTheme,
  TextInputType,
} from '@fcom/ui-components';
import { FileUploaderComponent } from '@fcom/ui-components/components/forms/file-uploader/file-uploader.component';

import {
  FormBuilderInput,
  type FormBuilderField,
  type FormBuilderGroup,
  type FormBuilderSubmitHandler,
} from '../interfaces';

@Component({
  selector: 'fin-form-builder',
  templateUrl: 'form-builder.component.html',
  styleUrls: ['./form-builder.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FormBuilderComponent implements OnInit, OnDestroy {
  readonly defaultAllowedFileTypes: FileUploaderComponent['allowedFileTypes'] = ATTACHMENT_ALLOWED_FILE_EXTENSIONS;
  readonly defaultMaxAttachments: FileUploaderComponent['maxAttachments'] = 1;
  readonly defaultMaxSizeInBytes: FileUploaderComponent['maxSizeInBytes'] = ATTACHMENT_MAX_SIZE_IN_BYTES;

  readonly ButtonType = ButtonType;
  readonly FormBuilderInput = FormBuilderInput;
  readonly FormStatus = FormStatus;
  readonly LoaderType = LoaderType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly TextInputType = TextInputType;

  @Input()
  controlConfig: FormBuilderGroup[];

  @Input()
  formName: string;

  @Input()
  translationNamespace = 'SERVICE_FORMS.serviceForms';

  @Input()
  translationPrefix: string;

  @Input({ required: true })
  submitHandler: FormBuilderSubmitHandler;

  @Input()
  hasSuccessMessage = true;

  @ViewChild('fileUploader') fileUploader: FileUploaderComponent;

  @ViewChild('formContainer', { static: true })
  formContainer: ElementRef;

  reactiveForm: TypedFormGroup<UntypedFormGroup>;
  formStatus: FormStatus = FormStatus.INITIAL;
  files: { [groupControlName: string]: { [fieldControlName: string]: FileList } } = {};
  prefillFields = {};

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private gtmService: GtmService,
    private prefillService: PrefillService
  ) {}

  ngOnInit(): void {
    this.reactiveForm = this.fb.group(
      this.controlConfig.reduce((groups, group: FormBuilderGroup): object => {
        groups[group.controlName] = new UntypedFormGroup(
          group.fields.reduce((fields, field: FormBuilderField) => {
            fields[field.controlName] = new UntypedFormControl(...field.controlConfig);
            if (field.prefillFromProfile) {
              this.prefillFields[field.prefillFromProfile] = field.controlName;
            }
            return fields;
          }, {})
        );
        return groups;
      }, {})
    ) as TypedFormGroup;

    this.prefillForm(this.reactiveForm);

    this.subscriptions.add(
      this.reactiveForm.statusChanges
        .pipe(
          filter((status) => status === 'INVALID'),
          distinctUntilChanged(),
          take(1)
        )
        .subscribe(() => {
          trackFormState(this.formName, 'start', this.gtmService);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  onSubmit(): void {
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }

    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();
      this.fileUploader?.ctrlField?.updateValueAndValidity();

      return;
    }

    this.formStatus = FormStatus.LOADING;
    this.subscriptions.add(
      this.submitHandler(this.reactiveForm.value)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.formStatus = FormStatus.COMPLETE;
            trackFormState(this.formName, 'success', this.gtmService);
          },
          error: () => {
            this.formStatus = FormStatus.ERROR;
            trackFormState(this.formName, 'error', this.gtmService);
          },
        })
    );
  }

  private prefillForm(form: UntypedFormGroup) {
    if (!isEmptyObjectOrHasEmptyValues(this.prefillFields)) {
      const groupControlName = Object.keys(form.controls)[0];
      const targetForm = form.controls[groupControlName] as UntypedFormGroup;

      Object.keys(this.prefillFields).forEach((field) => {
        this.prefillFields[field] = targetForm.get(this.prefillFields[field]);
      });

      this.subscriptions.add(this.prefillService.prefillFromProfile(targetForm, this.prefillFields).subscribe());
    }
  }
}
